<template>
  <div class="container py-3">
    <!-- Header Section with Responsive Layout -->
    <div class="header-section mb-3">
      <div class="flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
        <div class="table-info mb-2 mb-md-0">
          <h5 class="ref mb-1" v-if="tab.Name">
            <strong v-if="isQuote">REF : </strong>
            {{ tab.Name.toUpperCase() }}
            <span class="badge bg-info ms-1" v-if="tip > 0">Tip: {{ $utils.formatMoney(tip) }}</span>
          </h5>
          <h6 class="ref mb-0" v-if="isQuote && tab.Name">
            <strong>Quote No. {{ tab.saleNumber }}</strong>
          </h6>
        </div>
        <div class="action-buttons align-item-md-left mt-2 mt-md-0 ml-2">
          <button class="btn btn-primary w-100" @click="updateTable">
            <font-awesome-icon icon="save" class="me-1" />
            Save</button>
          <button class="btn btn-primary w-100" @click="pay">
            <font-awesome-icon icon="money-bill-wave" class="me-1" />
            Pay</button>

          <button class="btn btn-primary w-100" @click="checkSales">
            <font-awesome-icon icon="door-open" class="me-1" />
            Open Table
          </button>

          <button class="btn btn-info w-100" @click="printBillSlipAndroid" v-if="androidMode">
            <font-awesome-icon icon="receipt" class="me-1" />
            Bill Slip
          </button>

          <button class="btn btn-info w-100" @click="printBillSlip" v-if="!androidMode">
            <font-awesome-icon icon="receipt" class="me-1" />
            Bill Slip
          </button>
        </div>
      </div>
    </div>

    <!-- Selection Mode Banner -->
    <div class="selection-mode-banner" v-if="selectionMode">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <span class="selected-count">{{ selectedItems.length }} items selected</span>
        </div>
        <div class="selection-actions">
          <button class="btn btn-sm btn-outline-primary me-2" @click="selectAllItems">
            <font-awesome-icon icon="check-double" class="me-1" />
            Select All
          </button>
          <button class="btn btn-sm btn-outline-secondary me-2" @click="clearSelection">
            <font-awesome-icon icon="xmark" class="me-1" />
            Cancel
          </button>
          <button class="btn btn-sm btn-primary" @click="showTransferModal" :disabled="selectedItems.length === 0">
            <font-awesome-icon icon="right-left" class="me-1" />
            Transfer
          </button>
        </div>
      </div>
    </div>

    <!-- Toggle Selection Mode Button -->
    <div class="d-flex justify-content-end mb-2" v-if="!selectionMode">
      <button class="btn btn-outline-primary btn-sm" @click="toggleSelectionMode">
        <font-awesome-icon icon="check-square" class="me-1" />
        Select Items
      </button>
    </div>

    <!-- Search Bar when in Selection Mode -->
    <div class="mb-3" v-if="selectionMode">
      <div class="input-group">
        <span class="input-group-text">
          <font-awesome-icon icon="search" />
        </span>
        <input 
          type="text" 
          class="form-control" 
          placeholder="Search items..." 
          v-model="searchQuery"
          @input="filterItems"
        >
      </div>
    </div>

    <!-- Items List with Better Spacing -->
    <div class="content pb-5">
      <div 
        class="card mb-2" 
        v-for="item in filteredItems" 
        :key="item.Description" 
        @click="handleItemClick(item)"
        :class="{ 
          'selected': selectedItems.includes(item),
          'selectable': selectionMode
        }"
      >
        <div class="card-body p-2">
          <div class="item-header d-flex justify-content-between align-items-center mb-1">
            <div class="d-flex align-items-center" style="max-width: 70%;">
              <div class="selection-checkbox me-2" v-if="selectionMode">
                <input 
                  type="checkbox" 
                  class="form-check-input" 
                  :checked="selectedItems.includes(item)"
                  @click.stop
                >
              </div>
              <h6 class="mb-0 text-truncate">
                {{ $utils.toPascalCase($utils.cutLength(item.Description, 30)) }}
              </h6>
            </div>
            <div class="item-total fw-bold">{{ $utils.formatMoney(item.Line) }}</div>
          </div>

          <div class="item-details">
            <div class="d-flex justify-content-between mb-1">
              <span class="sum-line">{{ item.Qty }} × {{ $utils.formatMoney(item.Unit) }}</span>
              <TablesSaleQty @viewInstructions="viewInstructions" :sale-number="tab.saleNumber" :saleLine="item" />
            </div>

            <ul class="cooking-instruction-list ps-2 mb-0" v-if="item.instructions && item.instructions.length">
              <li class="cooking-instruction-list-item" v-for="instruction in item.instructions"
                :key="instruction.CookingMsg">
                <div class="d-flex justify-content-between">
                  <span>{{ instruction.CookingMsg }}</span>
                  <span class="ms-2">{{ instruction.count }} × {{ $utils.formatMoney(instruction.Price) }}</span>
                </div>
                <div class="text-end">Total: {{ $utils.formatMoney(instruction.count * instruction.Price) }}</div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="bottom-action-bar" :class="{ 'expanded': showBottomActions }">
      <div class="total-handle" @click="toggleBottomActions">
        <div class="d-flex justify-content-between align-items-center">
          <span class="total-label">Total:</span>
          <span class="total-amount">{{ $utils.formatMoney(tabTotal() + tip) }}</span>
          <font-awesome-icon :icon="showBottomActions ? 'chevron-down' : 'chevron-up'" class="ms-2" />
        </div>
      </div>

      <div class="action-content" v-show="showBottomActions">
        <div class="action-buttons-grid align-items-right">
          <button class="btn btn-info w-50" @click="printProforma" v-if="isQuote && !androidMode">
            <font-awesome-icon icon="receipt" class="me-1" />
            {{ (settings.PosType === 'Retail') ? 'Proforma Invoice' : 'Quote' }}
          </button>

          <button class="btn btn-primary w-40 m-2" @click="splitTable">
            <font-awesome-icon icon="arrows-split-up-and-left" class="me-1" />
            Split
          </button>

          <button class="btn btn-primary w-40 m-2" data-bs-toggle="modal"
            :data-bs-target="`#transferModal-${tab.saleNumber}`">
            <font-awesome-icon icon="right-left" class="me-1" />
            Transfer Table
          </button>

          <button class="btn btn-danger w-40 m-2" @click="voidTable">
            <font-awesome-icon icon="fa-solid fa-trash" class="me-1" />
            Void
          </button>
        </div>
      </div>
    </div>

    <!-- Transfer Modal -->
    <div class="modal fade" :id="`transferModal-${tab.saleNumber}`" tabindex="-1" aria-labelledby="transferModal"
      aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Assign Table To:</h5>
            <button type="button" class="btn-close" :id="`closeTransferModal-${tab.saleNumber}`" data-bs-dismiss="modal"
              aria-label="Close"></button>
          </div>
          <form @submit.prevent="transferTable">
            <div class="modal-body">
              <div class="mb-3">
                <label for="usersList" class="form-label">Select Waiter</label>
                <v-select id="usersList" v-model="assignUser" :options="users">
                </v-select>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              <button type="submit" class="btn btn-primary" :disabled="!assignUser">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Item Transfer Modal -->
    <div class="modal" id="itemTransferModal" tabindex="-1" aria-labelledby="itemTransferModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="itemTransferModalLabel">
              Transfer {{ selectedItems.length }} Items
            </h5>
            <button type="button" class="btn-close" @click="clearSelection" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="selected-items-summary mb-3" v-if="selectedItems.length > 0">
              <h6 class="mb-2">Selected Items:</h6>
              <div class="selected-items-list">
                <div class="selected-item" v-for="(item, index) in selectedItems.slice(0, 3)" :key="index">
                  <span class="item-name">{{ $utils.cutLength(item.Description, 30) }}</span>
                  <span class="item-price">{{ $utils.formatMoney(item.Line) }}</span>
                </div>
                <div class="more-items" v-if="selectedItems.length > 3">
                  <span>+ {{ selectedItems.length - 3 }} more items</span>
                </div>
              </div>
            </div>
            
            <div class="mb-3">
              <label for="tablesList" class="form-label">Select Target Table</label>
              <v-select 
                id="tablesList" 
                v-model="selectedTable" 
                :options="availableTablesForSelection" 
                label="Name"
                track-by="saleNumber"
                :filterable="true"
                placeholder="Search tables..."
              ></v-select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="clearSelection">Cancel</button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="confirmTransfer"
              :disabled="!selectedTable || selectedItems.length === 0"
            >
              Transfer Items
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Storage } from "@/services/Storage";
import TablesSaleQty from "@/components/point-of-sale/sales/TablesSaleQty.vue";
import { faSave, faMoneyBillWave, faDoorOpen, faChevronUp, faChevronDown, 
  faCheckSquare, faCheckDouble, faXmark, faSearch, faRightLeft } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { Modal } from 'bootstrap';

library.add(faSave, faMoneyBillWave, faDoorOpen, faChevronUp, faChevronDown, 
  faCheckSquare, faCheckDouble, faXmark, faSearch, faRightLeft);

export default {
  name: "TableDetails",
  components: { TablesSaleQty },
  beforeMount() {
    this.getTipTotal()
  },
  props: {
    tab: { type: Object, required: true },
  },
  data() {
    return {
      loading: false,
      tip: 0,
      assignUser: '',
      showBottomActions: false,
      allTables: [],
      selectedTable: null,
      selectedItems: [],
      itemTransferModal: null,
      selectionMode: false,
      searchQuery: '',
      filteredItems: []
    }
  },
  computed: {
    isQuote() {
      return !!(this.tab.customer && this.tab.customer !== '');
    },
    hasInstructions() {
      return (this.tab.instructions && this.tab.instructions.length > 0)
    },
    users() {
      return this.$store.getters.getUsers.map(user => user.Username);
    },
    settings() {
      return this.$store.getters.getDeviceSettings;
    },
    androidMode() {
      return this.$store.getters.getDeviceSettings?.androidMode || false;
    },
    availableTablesForSelection() {
      return this.allTables.map(table => ({
        ...table,
        // Ensure proper display format
        Name: table.Name || `Table ${table.saleNumber}`,
        // Add additional info for better selection
        displayText: `${table.Name || `Table ${table.saleNumber}`} (${this.$utils.formatMoney(table.total || 0)})`
      }));
    }
  },
  methods: {
    viewInstructions(plu) {
      return this.updateTable()
        .then(() => {
          this.$store.dispatch(`openTable`, this.tab)
          return this.$router.push({
            name: 'instruction',
            query: { plu }
          })
        })
    },
    async pay() {
      return this.updateTable()
        .then(async () => {

          await this.$store.dispatch(`openTable`, this.tab)
          return this.$router.push({
            name: 'payment',
          })
        })
    },

    async updateTable() {
      let saleLines = this.tab.items;
      let settings = await Storage.getDeviceSettings();
      for (let i = 0; i < saleLines.length; i++) {
        let line = saleLines[i];
        saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
      }
      let data = {
        SaleNum: this.tab.saleNumber,
        Name: this.tab.Name,
        User: this.tab.user,
        PC: settings.PC,
        items: saleLines,
        Customer: this.tab.customer,
        discount: parseFloat(this.$store.getters.getSaleDiscount.total),
      };

      this.loadPanel();
      return this.$store.dispatch('AddOrUpdateTable', data)
        .catch(err => errorHandler.tomcatError(err))
        .finally(() => {
          Swal.close()
        })
    },
    loadPanel() {
      return Swal.fire({
        html: 'Updating table....',
        allowOutsideClick: false,
        timerProgressBar: true,
        didOpen: () => {
          Swal.showLoading()
        },
      });
    },
    async splitTable() {
      let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `splitTable`)
      if (!isAllowed) return;
      return this.$router.push({
        name: `split-table`,
        query: { saleNumber: this.tab.saleNumber }
      })
    },
    async printBillSlip() {
      this.$utils.showLoading();
      return this.$store.dispatch(`printBillSlip`, this.tab.saleNumber)
        .finally(() => this.$utils.hideLoading())
    },
    async printBillSlipAndroid() {
      this.$utils.showLoading();
      return this.$store.dispatch(`printBillSlipAndroid`, this.tab.saleNumber)
        .finally(() => this.$utils.hideLoading())
    },
    async printProforma() {
      this.$utils.showLoading()
      return this.$store.dispatch(`printProforma`, this.tab.saleNumber)
        .finally(() => this.$utils.hideLoading())
    },
    async getTipTotal() {
      let saleTotal = this.tabTotal();
      let settings = await Storage.getDeviceSettings();
      let tip = 0;
      if (settings && settings.AutoTip > 0) {
        tip = Number(settings.AutoTip);
        tip = (tip / 100) * saleTotal;
      }
      this.tip = tip;
    },
    tabTotal() {
      let sum = 0;
      let items = this.tab.items;
      if (!items) return 0.00;
      for (let i = 0; i < items.length; i++) {
        sum += items[i].Line;
        let instructions = items[i].instructions || [];
        for (let k = 0; k < instructions.length; k++) {
          let price = instructions[k].Price;
          let count = instructions[k].count || 1;
          sum += price * count;
        }
      }
      return sum;
    },

    checkSales() {
      localStorage.removeItem('currentSaleLines')
      let sale = this.$store.getters.getSaleLines;
      if (sale.length > 0) {
        Swal.fire({
          title: 'Are you sure?',
          text: "Opening this table will override the current sale",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, Override it!'
        }).then((result) => {
          if (result.isConfirmed) {
            this.selectTab();
          }
        })
      } else {
        this.selectTab()
      }
    },
    selectTab() {
      return this.$store.dispatch('validateSecurityPermission', `LoadTable`)
        .then((res) => {
          if (res) {
            return this.$router.push({
              name: 'pos',
              query: { saleNumber: this.tab.saleNumber }
            });
          }
        });
    },

    async voidTable() {
      return Swal.fire({
        title: 'Are you sure?',
        text: "This step cannot be reversed!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, Void it!',
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return this.$store.dispatch('removeTable', this.tab)
            .finally(() => this.$emit('updateAction'))
        },
      }).then((result) => {
        if (result.isConfirmed) {
          return responses.showInfo(`Table Voided Successfully`)
        }
      })
    },

    transferTable() {
      document.getElementById(`closeTransferModal-${this.tab.saleNumber}`).click();
      let obj = {
        saleNumber: this.tab.saleNumber,
        transferredTo: this.assignUser,
      }
      return this.$store.dispatch(`transferTable`, obj)
        .then((res) => {
          if (res) return this.$emit('updateAction');
        })
    },
    toggleBottomActions() {
      this.showBottomActions = !this.showBottomActions;
    },
    
    // New Methods for Item Selection and Transfer
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.clearSelection();
      }
      this.filterItems();
    },
    
    handleItemClick(item) {
      if (this.selectionMode) {
        this.toggleItemSelection(item);
      }
    },
    
    toggleItemSelection(item) {
      const index = this.selectedItems.indexOf(item);
      if (index === -1) {
        this.selectedItems.push(item);
      } else {
        this.selectedItems.splice(index, 1);
      }
    },
    
    selectAllItems() {
      // Select all currently filtered items
      if (this.filteredItems.length === this.selectedItems.length) {
        // If all are selected, deselect all
        this.selectedItems = [];
      } else {
        // Select all filtered items
        this.selectedItems = [...this.filteredItems];
      }
    },
    
    clearSelection() {
      this.selectedItems = [];
      this.selectedTable = null;
      this.closeTransferModal();
    },
    
    async showTransferModal() {
      if (this.selectedItems.length === 0) return;
      
      // Refresh available tables before showing modal
      await this.loadAvailableTables();
      
      // Dispose previous modal instance if it exists
      if (this.itemTransferModal) {
        this.itemTransferModal.dispose();
        this.itemTransferModal = null;
      }
      
      // Create new modal instance
      const modalElement = document.getElementById('itemTransferModal');
      this.itemTransferModal = new Modal(modalElement, {
        backdrop: 'static',
        keyboard: false
      });
      this.itemTransferModal.show();
    },
    
    filterItems() {
      if (!this.searchQuery.trim()) {
        this.filteredItems = this.tab.items || [];
        return;
      }
      
      const query = this.searchQuery.toLowerCase().trim();
      this.filteredItems = (this.tab.items || []).filter(item => 
        item.Description.toLowerCase().includes(query)
      );
    },
    
    async confirmTransfer() {
      if (!this.selectedTable || this.selectedItems.length === 0) return;

      // Show loading state
      Swal.fire({
        title: 'Preparing Transfer...',
        text: 'Please wait while we process your request',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      try {
        // Refresh tables data first
        await this.$store.dispatch('getAllTables');

        const payload = {
          fromSaleNumber: Number(this.tab.saleNumber),
          toSaleNumber: Number(this.selectedTable.saleNumber),
          items: this.selectedItems.map(item => ({
            ...item,
            Plu: item.Plu,
            Description: item.Description,
            Qty: Number(item.Qty),
            Unit: Number(item.Unit),
            Line: Number(item.Line)
          }))
        };

        await this.$store.dispatch('transferItems', payload);
        
        // Store table name before clearing selection
        const targetTableName = this.selectedTable?.Name || 'target table';
        
        // Close modal AFTER successful transfer
        this.closeTransferModal();
        
        // Reset state
        this.selectedItems = [];
        this.selectionMode = false;
        this.selectedTable = null;
        
        // Update parent component
        this.$emit('updateAction');
        
        // Show success message
        Swal.fire({
          icon: 'success',
          title: 'Items Transferred',
          text: `Successfully transferred ${payload.items.length} items to ${targetTableName}`,
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Transfer error in component:', error);
        let errorMessage = 'There was an error transferring the items.';
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Close modal on error too
        this.closeTransferModal();
        
        Swal.fire({
          icon: 'error',
          title: 'Transfer Failed',
          text: errorMessage,
          showConfirmButton: true
        });
      }
    },

    handleKeyDown(event) {
      if (event.key === ' ' && this.selectionMode) {
        event.preventDefault(); // Prevent default space behavior (e.g., scrolling)
        this.showTransferModal(); // Open the transfer confirmation modal
      }
    },

    async loadAvailableTables() {
      try {
        // Get fresh table data from the server
        await this.$store.dispatch('getAllTables');
        
        // Get all tables from store and filter out the current table
        const allTables = this.$store.getters.getAllTables || [];
        
        // Filter out the current table from the available options
        this.allTables = allTables.filter(table => 
          Number(table.saleNumber) !== Number(this.tab.saleNumber)
        );
        
        console.log('Available tables for transfer:', this.allTables);
      } catch (error) {
        console.error('Error loading tables:', error);
        this.allTables = [];
      }
    },

    closeTransferModal() {
      try {
        // Hide the modal first
        if (this.itemTransferModal) {
          this.itemTransferModal.hide();
          // Wait a moment for the animation to complete
          setTimeout(() => {
            this.itemTransferModal.dispose();
            this.itemTransferModal = null;
          }, 300);
        }
        
        // Clean up any remaining modal artifacts
        const modalElement = document.getElementById('itemTransferModal');
        if (modalElement) {
          modalElement.classList.remove('show');
          modalElement.style.display = 'none';
          modalElement.setAttribute('aria-hidden', 'true');
          modalElement.removeAttribute('aria-modal');
        }
        
        // Remove backdrop if it exists
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }
        
        // Remove modal-open class from body
        document.body.classList.remove('modal-open');
        
        // Reset overflow style
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        
      } catch (error) {
        console.error('Error closing transfer modal:', error);
        // Force cleanup even if there's an error
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) backdrop.remove();
      }
    }
  },
  async mounted() {
    // Load tables for transfer functionality
    await this.loadAvailableTables();
    this.filteredItems = this.tab.items || [];
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  watch: {
    'tab.items': {
      immediate: true,
      handler(newItems) {
        this.filteredItems = newItems || [];
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Header section styling
.header-section {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding: 15px 0;
  border-bottom: 1px solid #eee;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .table-info {
    padding: 0 10px;
    
    .ref {
      color: #2c3e50;
      margin-bottom: 0.5rem;
    }
  }
}

// Selection Mode Banner
.selection-mode-banner {
  background: #e3f2fd;
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  .selected-count {
    font-weight: 600;
    color: #1565c0;
  }
  
  .selection-actions {
    display: flex;
    align-items: center;
  }
}

// Action buttons improvements
.action-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 8px;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  }

  button {
    padding: 8px 12px;
    font-size: 0.85rem;
    height: 40px;
    border-radius: 8px;
    transition: all 0.2s ease;
    margin: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:active {
      transform: translateY(1px);
    }

    .me-1 {
      margin-right: 8px;
    }
  }
}

// Content area improvements
.content {
  padding: 15px 10px;
  margin-bottom: 60px; // Reduced margin for collapsed state

  .card {
    border-radius: 12px;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
    margin-bottom: 12px;

    &.selectable {
      cursor: pointer;
      
      &:hover {
        background-color: #f9f9f9;
      }
    }
    
    &.selected {
      border: 2px solid #1e88e5;
      background-color: #e3f2fd;
      
      .item-header h6 {
        color: #1565c0;
      }
    }

    .card-body {
      padding: 12px 16px;
    }

    .item-header {
      h6 {
        font-size: 1rem;
        color: #2c3e50;
      }

      .item-total {
        color: #1e88e5;
        font-size: 1.1rem;
      }
    }

    .item-details {
      .sum-line {
        color: #666;
        font-size: 0.9rem;
      }
    }
  }
}

// Updated Bottom Action Bar
.bottom-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: 100;
  transition: transform 0.3s ease-in-out;

  // Total Handle Styling
  .total-handle {
    padding: 12px 16px;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    user-select: none;
    
    &:active {
      background: #f8f9fa;
    }

    .total-label {
      font-size: 1.1rem;
      color: #2c3e50;
    }

    .total-amount {
      font-size: 1.3rem;
      color: #1e88e5;
      font-weight: 700;
    }
  }

  // Action Content (sliding part)
  .action-content {
    background: white;
    padding: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  }

  &.expanded {
    .action-content {
      animation: slideUp 0.3s ease-out forwards;
    }
  }
}

// Animation for sliding up
@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// Cooking instruction list improvements
.cooking-instruction-list {
  list-style: none;
  margin-top: 8px;
  padding-left: 12px;
  border-left: 2px solid #e0e0e0;

  .cooking-instruction-list-item {
    font-size: 0.85rem;
    color: #666;
    margin-bottom: 6px;
    padding: 4px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Modal improvements
.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  .modal-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px 20px;

    .modal-title {
      font-size: 1.2rem;
      color: #2c3e50;
    }
  }

  .modal-body {
    padding: 20px;
  }

  .modal-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px 20px;
  }
}

// Selection checkbox
.selection-checkbox {
  .form-check-input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    
    &:checked {
      background-color: #1e88e5;
      border-color: #1e88e5;
    }
  }
}

// Selected items summary in modal
.selected-items-summary {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  
  h6 {
    color: #2c3e50;
    font-size: 0.9rem;
  }
  
  .selected-items-list {
    .selected-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      font-size: 0.85rem;
      
      .item-price {
        font-weight: 600;
        color: #1e88e5;
      }
    }
    
    .more-items {
      text-align: center;
      font-size: 0.8rem;
      color: #666;
      padding-top: 8px;
    }
  }
}

// General button improvements
.btn {
  font-weight: 600;
  letter-spacing: 0.3px;
  border: none;
  
  &-primary {
    background: #1e88e5;
    &:hover {
      background: #1976d2;
    }
  }
  
  &-danger {
    background: #f44336;
    &:hover {
      background: #d32f2f;
    }
  }
  
  &-info {
    background: #03a9f4;
    color: white;
    &:hover {
      background: #0288d1;
      color: white;
    }
  }
  
  &-outline-primary {
    border: 1px solid #1e88e5;
    color: #1e88e5;
    &:hover {
      background: #e3f2fd;
      color: #1565c0;
    }
  }
}
</style>